/**
 * Training Job Detail Page
 *
 * Shows training job status, progress, configuration, and metrics.
 */

import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Progress,
  Spinner,
} from '@heroui/react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Brain,
  CheckCircle,
  Clock,
  Database,
  Loader2,
  Settings,
  Tag,
  XCircle,
  Zap,
} from 'lucide-react';

import { useTrainingJob, useCancelTrainingJob } from '@/lib/api/hooks/training';
import { useDataset } from '@/lib/api/hooks/datasets';

export const Route = createFileRoute('/training/$jobId')({
  component: TrainingJobDetailPage,
});

function TrainingJobDetailPage() {
  const { jobId } = Route.useParams();
  const navigate = useNavigate();

  const { data: job, isLoading: isLoadingJob } = useTrainingJob(jobId);
  const { data: dataset } = useDataset(job?.dataset_id || '');
  const cancelJob = useCancelTrainingJob();

  if (isLoadingJob) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="container-page py-6">
        <Card className="border border-default-100 shadow-none">
          <CardBody className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="h-12 w-12 text-danger mb-4" />
            <p className="text-lg font-medium">Job not found</p>
            <Button
              variant="flat"
              size="sm"
              className="mt-4"
              onPress={() => navigate({ to: '/training' })}
            >
              Back to Training Jobs
            </Button>
          </CardBody>
        </Card>
      </div>
    );
  }

  const config = job.config as {
    artifact_types?: string[];
    epochs?: number;
    learning_rate?: number;
    batch_size?: number;
    validation_split?: number;
    chunk_size_ms?: number;
    stride_ms?: number;
  };

  const metrics = job.metrics as {
    training_loss?: number;
    validation_loss?: number;
    accuracy?: number;
    per_class_metrics?: Record<
      string,
      { precision: number; recall: number; f1: number }
    >;
  } | null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'training':
      case 'pending':
        return 'primary';
      case 'failed':
      case 'cancelled':
        return 'danger';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4" />;
      case 'training':
        return <Loader2 className="h-4 w-4 animate-spin" />;
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'failed':
      case 'cancelled':
        return <XCircle className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const isInProgress = job.status === 'pending' || job.status === 'training';

  const handleCancel = async () => {
    try {
      await cancelJob.mutateAsync(jobId);
    } catch {
      // Error handled by mutation
    }
  };

  return (
    <div className="container-page py-6">
      {/* Header */}
      <Button
        variant="light"
        size="sm"
        startContent={<ArrowLeft className="h-4 w-4" />}
        onPress={() => navigate({ to: '/training' })}
        className="mb-4"
      >
        Back to Training Jobs
      </Button>

      <div className="mb-6 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-bold">Training Job</h1>
            <Chip
              color={getStatusColor(job.status)}
              variant="flat"
              size="sm"
              startContent={getStatusIcon(job.status)}
            >
              {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
            </Chip>
          </div>
          <p className="text-sm text-default-500 font-mono">{job.id}</p>
        </div>
        {isInProgress && (
          <Button
            color="danger"
            variant="flat"
            size="sm"
            onPress={handleCancel}
            isLoading={cancelJob.isPending}
          >
            Cancel Training
          </Button>
        )}
      </div>

      {/* Progress */}
      {isInProgress && (
        <Card className="border border-primary/20 bg-primary/5 shadow-none mb-6">
          <CardBody className="px-4 py-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
                <span className="text-sm font-medium text-primary">
                  {job.status === 'pending' ? 'Preparing...' : 'Training in progress...'}
                </span>
              </div>
              <span className="text-sm font-mono text-primary">
                {job.progress_percent}%
              </span>
            </div>
            <Progress
              size="md"
              value={job.progress_percent}
              color="primary"
              className="max-w-full"
            />
            {job.status_message && (
              <p className="text-xs text-default-500 mt-2">{job.status_message}</p>
            )}
          </CardBody>
        </Card>
      )}

      {/* Error Message */}
      {job.status === 'failed' && job.error_message && (
        <Card className="border border-danger/20 bg-danger/5 shadow-none mb-6">
          <CardBody className="px-4 py-4">
            <div className="flex items-start gap-3">
              <XCircle className="h-5 w-5 text-danger flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-danger">Training Failed</p>
                <p className="text-xs text-default-500 mt-1">{job.error_message}</p>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Success - Link to Inference */}
      {job.status === 'completed' && (
        <Card className="border border-success/20 bg-success/5 shadow-none mb-6">
          <CardBody className="px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-success/10">
                  <Zap className="h-5 w-5 text-success" />
                </div>
                <div>
                  <p className="text-sm font-medium text-success">
                    Training Complete
                  </p>
                  <p className="text-xs text-default-500">
                    Your model is ready for inference
                  </p>
                </div>
              </div>
              <Button
                as={Link}
                to="/inference/new"
                color="success"
                size="sm"
                endContent={<ArrowRight className="h-4 w-4" />}
              >
                Run Inference
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Dataset Info */}
        <Card className="border border-default-100 shadow-none">
          <CardHeader className="gap-3 pb-1 pt-3 px-4">
            <Database className="h-4 w-4 text-default-500" />
            <h3 className="text-sm font-medium">Dataset</h3>
          </CardHeader>
          <CardBody className="px-4 pb-4 pt-2">
            {dataset ? (
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <Database className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">{dataset.name}</p>
                  <p className="text-xs text-default-500">
                    {dataset.description || 'No description'}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-default-500">Loading...</p>
            )}
          </CardBody>
        </Card>

        {/* Artifact Types */}
        <Card className="border border-default-100 shadow-none">
          <CardHeader className="gap-3 pb-1 pt-3 px-4">
            <Tag className="h-4 w-4 text-default-500" />
            <h3 className="text-sm font-medium">Artifact Types</h3>
          </CardHeader>
          <CardBody className="px-4 pb-4 pt-2">
            <div className="flex flex-wrap gap-2">
              {config.artifact_types?.map((type) => (
                <Chip key={type} size="sm" variant="flat">
                  {type}
                </Chip>
              )) || <p className="text-sm text-default-500">None specified</p>}
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Training Configuration */}
      <Card className="border border-default-100 shadow-none mb-6">
        <CardHeader className="gap-3 pb-1 pt-3 px-4">
          <Settings className="h-4 w-4 text-default-500" />
          <h3 className="text-sm font-medium">Training Configuration</h3>
        </CardHeader>
        <CardBody className="px-4 pb-4 pt-2">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-default-500">Epochs</p>
              <p className="text-sm font-medium font-mono">{config.epochs ?? 10}</p>
            </div>
            <div>
              <p className="text-xs text-default-500">Learning Rate</p>
              <p className="text-sm font-medium font-mono">{config.learning_rate ?? 0.001}</p>
            </div>
            <div>
              <p className="text-xs text-default-500">Batch Size</p>
              <p className="text-sm font-medium font-mono">{config.batch_size ?? 32}</p>
            </div>
            <div>
              <p className="text-xs text-default-500">Validation Split</p>
              <p className="text-sm font-medium font-mono">
                {((config.validation_split ?? 0.2) * 100).toFixed(0)}%
              </p>
            </div>
            <div>
              <p className="text-xs text-default-500">Chunk Size</p>
              <p className="text-sm font-medium font-mono">{config.chunk_size_ms ?? 500}ms</p>
            </div>
            <div>
              <p className="text-xs text-default-500">Stride</p>
              <p className="text-sm font-medium font-mono">{config.stride_ms ?? 250}ms</p>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Training Metrics */}
      {metrics && (
        <Card className="border border-default-100 shadow-none">
          <CardHeader className="gap-3 pb-1 pt-3 px-4">
            <Brain className="h-4 w-4 text-default-500" />
            <h3 className="text-sm font-medium">Training Metrics</h3>
          </CardHeader>
          <CardBody className="px-4 pb-4 pt-2">
            {/* Summary Metrics */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              {metrics.training_loss !== undefined && (
                <div className="rounded-lg bg-default-50 p-3 text-center">
                  <p className="text-xs text-default-500">Training Loss</p>
                  <p className="text-lg font-bold font-mono">
                    {metrics.training_loss.toFixed(4)}
                  </p>
                </div>
              )}
              {metrics.validation_loss !== undefined && (
                <div className="rounded-lg bg-default-50 p-3 text-center">
                  <p className="text-xs text-default-500">Validation Loss</p>
                  <p className="text-lg font-bold font-mono">
                    {metrics.validation_loss.toFixed(4)}
                  </p>
                </div>
              )}
              {metrics.accuracy !== undefined && (
                <div className="rounded-lg bg-success/10 p-3 text-center">
                  <p className="text-xs text-default-500">Accuracy</p>
                  <p className="text-lg font-bold font-mono text-success">
                    {(metrics.accuracy * 100).toFixed(1)}%
                  </p>
                </div>
              )}
            </div>

            {/* Per-class Metrics */}
            {metrics.per_class_metrics && Object.keys(metrics.per_class_metrics).length > 0 && (
              <div>
                <p className="text-xs text-default-500 mb-2">Per-Class Performance</p>
                <div className="space-y-2">
                  {Object.entries(metrics.per_class_metrics).map(([className, classMetrics]) => (
                    <div
                      key={className}
                      className="flex items-center justify-between rounded-lg border border-default-100 p-3"
                    >
                      <span className="text-sm font-medium">{className}</span>
                      <div className="flex gap-4 text-xs">
                        <div>
                          <span className="text-default-500">Precision: </span>
                          <span className="font-mono">
                            {(classMetrics.precision * 100).toFixed(1)}%
                          </span>
                        </div>
                        <div>
                          <span className="text-default-500">Recall: </span>
                          <span className="font-mono">
                            {(classMetrics.recall * 100).toFixed(1)}%
                          </span>
                        </div>
                        <div>
                          <span className="text-default-500">F1: </span>
                          <span className="font-mono font-medium text-primary">
                            {(classMetrics.f1 * 100).toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
}
